import { useSyncExternalStore } from 'react';

/**
 * ЭТАП 2 — фаза гидрации.
 *
 * SSR рендерит контент (в т.ч. внутри LazyRender), и ПЕРВЫЙ клиентский рендер
 * обязан выдать ту же разметку — иначе React выкинет серверный HTML (mismatch).
 * После гидрации (SPA-навигации) ленивые блоки возвращаются к обычному
 * поведению «рендер по доскроллу».
 *
 * markHydrated() вызывается из RootEffects (первый useEffect после гидрации).
 * Компоненты, латчащие форс-рендер, должны читать isHydrationPhase() в
 * useState-инициализаторе — значение фиксируется на маунте и не меняется.
 */
let hydrated = false;

export const markHydrated = (): void => {
  hydrated = true;
  // Класс-гейт для pre-hydration CSS (keenSliderSSR.scss): правила вида
  // `html:not(.app-hydrated) ...` действуют строго до этого момента и
  // ФИЗИЧЕСКИ не могут конфликтовать с инлайн-стилями keen-slider после
  // инициализации. Ставим императивно — React <html> className не управляет
  // (в JSX __root класса нет), поэтому ре-рендеры его не восстановят.
  if (typeof document !== 'undefined') {
    document.documentElement.classList.add('app-hydrated');
  }
};

export const isHydrationPhase = (): boolean =>
  typeof window === 'undefined' || !hydrated;

/**
 * Гидрируется ли ЭТОТ компонент прямо сейчас (или рендерится на сервере).
 *
 * ВАЖНО: глобальный isHydrationPhase()/markHydrated() для КОМПОНЕНТОВ не
 * годится — флаг снимается эффектом RootEffects после гидрации оболочки, а
 * страницы за Suspense/lazy гидрируются ПОЗЖЕ (selective hydration) и увидели
 * бы «фаза кончилась» → мисматч (наблюдалось на LazyRender главной).
 * useSyncExternalStore лишён гонки: server-snapshot (true) React использует
 * именно в гидрационном рендере данного компонента, сколь угодно позднем;
 * после гидрации — клиентский snapshot (false).
 * Латчить в useState-инициализаторе, если значение должно пережить ре-рендеры.
 */

const noopSubscribe = (): (() => void) => () => {};

export const useIsHydrationRender = (): boolean =>
  useSyncExternalStore(
    noopSubscribe,
    () => false,
    () => true
  );
